import { useId, useMemo, useRef } from 'react'
import {
  AgGridReact,
  useGridCellEditor,
  type CustomCellEditorProps,
} from 'ag-grid-react'
import {
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
  type ICellRendererParams,
  type IHeaderParams,
} from 'ag-grid-community'
import { LockKeyhole, Pencil } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import {
  editablePercent,
  formatPercent,
  formatUnits,
  formatWaveDate,
  samePercent,
  validateWavePercent,
  weekLabel,
  type CalculatedWaveWeek,
  type WaveWeek,
} from './model'
import { dropInvalidPercent, useSpreadsheetStart } from './gridEditing'
import './wave-planning.css'

ModuleRegistry.registerModules([AllCommunityModule])

interface MetricRow {
  id: string
  label: string
  kind: 'percent' | 'units'
  editable?: boolean
  reference?: boolean
  description: string
  values: (number | null)[]
}

function PercentEditor(
  props: CustomCellEditorProps<MetricRow, number | string>
) {
  const input = useRef<HTMLInputElement>(null)
  const errorId = useId()
  const value =
    typeof props.value === 'number'
      ? editablePercent(props.value)
      : String(props.value ?? '')
  const error = validateWavePercent(value)

  useSpreadsheetStart(props.eventKey, props.onValueChange, input)

  useGridCellEditor({
    isCancelAfterEnd: () => dropInvalidPercent(input.current?.value ?? value),
  })

  return (
    <div
      className="relative h-full w-full"
      onKeyDownCapture={event => {
        if (event.key !== 'Escape') return
        event.preventDefault()
        event.stopPropagation()
        props.api.stopEditing(true)
        if (props.node.rowIndex !== null)
          props.api.setFocusedCell(props.node.rowIndex, props.column)
      }}
    >
      <div className="relative h-full">
        <Input
          ref={input}
          aria-label={`${props.colDef.headerName} · Planned Wave %`}
          value={value}
          inputMode="decimal"
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          title={error ?? undefined}
          className="h-full w-full rounded-none border-0 pr-6 text-right font-semibold tabular-nums shadow-none focus-visible:ring-2 focus-visible:ring-inset aria-invalid:ring-2 aria-invalid:ring-inset aria-invalid:ring-destructive"
          onChange={event => props.onValueChange(event.target.value)}
        />
        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
          %
        </span>
      </div>
      {error && (
        <p id={errorId} role="alert" className="sr-only">
          {error}
        </p>
      )}
    </div>
  )
}

function MetricLabel({ data }: ICellRendererParams<MetricRow>) {
  if (!data) return null
  return (
    <div className="wave-metric-content flex w-full items-center justify-between gap-2">
      <span className="wave-metric-name truncate text-xs font-medium">
        {data.label}
      </span>
      {data.editable && (
        <span className="flex shrink-0 items-center gap-1 text-[10px] font-semibold text-primary">
          <Pencil size={11} />
          Editable
        </span>
      )}
    </div>
  )
}

function WeekHeader({
  week,
  isSet,
}: IHeaderParams & { week: WaveWeek; isSet: boolean }) {
  return (
    <div className="flex w-full flex-col items-end gap-0.5 text-xs">
      <span className={isSet ? 'font-semibold text-primary' : 'font-semibold'}>
        {week.event}
        {isSet && ' · SET'}
      </span>
      <span>{formatWaveDate(week.date)}</span>
      <span className="text-[10px] font-normal text-muted-foreground">
        {weekLabel(week.week)}
      </span>
    </div>
  )
}

interface Props {
  weeks: CalculatedWaveWeek[]
  timeline: WaveWeek[]
  year: number
  /** Timeline position of the set week, or -1. */
  setIndex: number
  onChange: (weekId: string, value: string) => void
  readOnly?: boolean
  /** Receipt-to-sales threshold as a fraction (Seasonal); null when not used. */
  varianceThreshold?: number | null
  /** The metric rows follow the SKU-calculated waves (else the plan percentages). */
  skuBasis?: boolean
}

// Column ids use the timeline position: week numbers can repeat across years.
const weekColumnId = (index: number) => `week-${index}`

const variancePercentLabel = (threshold: number | null) =>
  threshold === null ? 'configured' : formatPercent(threshold, 0)

export function WavePlanningGrid({
  weeks,
  timeline,
  year,
  setIndex,
  onChange,
  readOnly = false,
  varianceThreshold = null,
  skuBasis = false,
}: Props) {
  const narrow = useMediaQuery('(max-width: 767px)')
  const mobile = useMediaQuery('(max-width: 639px)')
  const rows = useMemo<MetricRow[]>(() => {
    const yy = String(year).slice(-2)
    const metric = (
      id: keyof CalculatedWaveWeek,
      label: string,
      kind: MetricRow['kind'],
      description: string,
      extras = {}
    ): MetricRow => ({
      id,
      label,
      kind,
      description,
      ...extras,
      // A missing value (e.g. a SKU-calculated wave of an old frozen version) shows "—".
      values: weeks.map(week =>
        week[id] == null
          ? null
          : Number(week[id]) * (kind === 'percent' ? 100 : 1)
      ),
    })
    return [
      metric(
        'historicalWave',
        `${String(year - 1).slice(-2)} Actual Wave % Ordered`,
        'percent',
        'Historical wave distribution. Read-only reference.',
        { reference: true }
      ),
      metric(
        'cumulativeDemandPercent',
        `${yy} Cum D/T %`,
        'percent',
        'Cumulative demand divided by planned purchase units.'
      ),
      metric(
        'plannedWave',
        `${yy} Planned Wave %`,
        'percent',
        'Enter the share of purchase units allocated to each wave.',
        // Read-only plans (submitted, frozen, Core) show it as a plain metric.
        { editable: !readOnly }
      ),
      metric(
        'skuWave',
        `${yy} SKU-Calculated Wave %`,
        'percent',
        'Bottoms-up: what the SKUs plan in each week (their wave % weighted by quantity). It differs from the plan where SKUs were changed or raised to their POG set.'
      ),
      metric(
        'cumulativeWave',
        `${yy} Cum Planned Wave %`,
        'percent',
        'Sum of planned waves through this week.'
      ),
      metric(
        'averageReceipts',
        `${yy} Avg Store Unit Receipts`,
        'units',
        'Weekly planned receipts divided by store count.'
      ),
      metric(
        'receipts',
        `${yy} Planned Unit Receipts`,
        'units',
        skuBasis
          ? 'SKU-calculated wave percentage multiplied by purchase units.'
          : 'Planned wave percentage multiplied by purchase units.'
      ),
      metric(
        'inventory',
        `${yy} Planned Units EOW OH`,
        'units',
        'Previous inventory plus receipts minus weekly demand.'
      ),
      metric(
        'averageInventory',
        `${yy} Avg Store Units EOW OH`,
        'units',
        'End-of-week inventory divided by store count.'
      ),
      metric(
        'receiptsInStores',
        'Planned Receipts in Stores %',
        'percent',
        skuBasis
          ? 'Prior cumulative SKU-calculated waves plus half of the current one.'
          : 'Prior cumulative waves plus half of the current wave.'
      ),
      metric(
        'salesInStores',
        'Planned Sales in Stores %',
        'percent',
        'Cumulative demand divided by total period demand.'
      ),
      metric(
        'variance',
        'Variance · Receipts − Sales',
        'percent',
        'Receipts percentage minus sales percentage for the same week.'
      ),
    ]
  }, [weeks, year, readOnly, skuBasis])

  const columns = useMemo<ColDef<MetricRow>[]>(
    () => [
      {
        colId: 'metric',
        field: 'label',
        headerName: `${year} Plan`,
        pinned: 'left',
        lockPinned: true,
        initialWidth: mobile ? 145 : narrow ? 220 : 285,
        cellRenderer: MetricLabel,
        tooltipValueGetter: params => params.data?.description,
        cellClass: params =>
          params.data?.editable ? 'wave-input-label' : 'wave-metric-label',
      },
      ...timeline.map(
        (week, index): ColDef<MetricRow> => ({
          colId: weekColumnId(index),
          headerName: weekLabel(week.week),
          initialWidth: 110,
          headerComponent: WeekHeader,
          headerComponentParams: { week, isSet: index === setIndex },
          headerClass: index === setIndex ? 'wave-set-header' : undefined,
          cellDataType: false,
          valueGetter: params => params.data?.values[index],
          valueFormatter: params =>
            params.value == null
              ? '—'
              : params.data?.kind === 'percent'
                ? formatPercent(
                    Number(params.value) / 100,
                    params.data.id === 'cumulativeDemandPercent' ? 1 : 2
                  )
                : formatUnits(Number(params.value)),
          editable: params =>
            !!params.data?.editable && week.event !== 'NO WAVE',
          cellEditor: PercentEditor,
          suppressKeyboardEvent: params => {
            const target = params.event.target
            // AG Grid handles native key events before React's bubbling handlers.
            // Keep an invalid editor open on Enter/Tab; Escape still cancels normally.
            if (
              params.editing &&
              (params.event.key === 'Enter' || params.event.key === 'Tab') &&
              target instanceof HTMLInputElement &&
              validateWavePercent(target.value)
            ) {
              params.event.preventDefault()
              return true
            }
            return false
          },
          cellClass: params =>
            [
              'ag-right-aligned-cell',
              'wave-number',
              params.data?.editable
                ? week.event === 'NO WAVE'
                  ? 'wave-blocked-cell'
                  : 'wave-input-cell'
                : '',
              params.data?.reference ? 'wave-reference-cell' : '',
              (params.data?.id === 'inventory' ||
                params.data?.id === 'variance') &&
              Number(params.value) < -0.000001
                ? 'wave-negative-cell'
                : '',
              params.data?.id === 'variance' && weeks[index]?.thresholdCrossed
                ? 'wave-trigger-cell'
                : '',
              params.data?.id === 'skuWave' &&
              weeks[index]?.skuWave != null &&
              !samePercent(
                weeks[index].skuWave * 100,
                weeks[index].plannedWave * 100
              )
                ? 'wave-sku-diff-cell'
                : '',
            ].filter(Boolean),
          tooltipValueGetter: params =>
            week.event === 'NO WAVE'
              ? 'No wave is scheduled for this week.'
              : params.data?.id === 'variance' && weeks[index]?.thresholdCrossed
                ? `Variance fell below the ${variancePercentLabel(varianceThreshold)} receipt-to-sales threshold: recommended timing for the next wave.`
                : params.data?.description,
        })
      ),
    ],
    [timeline, weeks, year, setIndex, narrow, mobile, varianceThreshold]
  )

  return (
    <>
      <div
        className="wave-planning-grid ag-theme-michaels h-[550px] w-full"
        aria-label="Weekly wave planning"
      >
        <AgGridReact<MetricRow>
          theme="legacy"
          rowData={rows}
          columnDefs={columns}
          getRowId={params => params.data.id}
          defaultColDef={{
            sortable: false,
            filter: false,
            resizable: true,
            suppressMovable: true,
          }}
          rowHeight={mobile ? 58 : 42}
          headerHeight={70}
          singleClickEdit
          readOnlyEdit
          invalidEditValueMode="revert"
          stopEditingWhenCellsLoseFocus
          ensureDomOrder
          tooltipShowDelay={450}
          onCellEditRequest={event => {
            const index = Number(event.column.getColId().replace('week-', ''))
            const week = timeline[index]
            const text = String(event.newValue ?? '')
            // An unchanged value is not saved: committing a cell must not turn
            // a recommendation into planner input or rewrite a stored value.
            if (
              !week ||
              samePercent(
                text.trim() ? Number(text) : 0,
                Number(event.oldValue)
              )
            )
              return
            onChange(week.id, text)
          }}
          onGridReady={event => {
            if (setIndex >= 0)
              event.api.ensureColumnVisible(
                weekColumnId(Math.max(0, setIndex - (mobile ? 0 : 1))),
                'start'
              )
          }}
          tabToNextCell={params => {
            if (!params.editing) return params.nextCellPosition ?? false
            const current = Number(
              params.previousCellPosition.column.getColId().replace('week-', '')
            )
            const direction = params.backwards ? -1 : 1
            let next = current + direction
            while (weeks[next]?.event === 'NO WAVE') next += direction
            const column =
              weeks[next] && params.api.getColumn(weekColumnId(next))
            return column
              ? {
                  rowIndex: params.previousCellPosition.rowIndex,
                  rowPinned: null,
                  column,
                }
              : false
          }}
        />
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2 border-t bg-secondary/40 px-4 py-2.5 text-xs text-muted-foreground">
        <span>
          {timeline.length} weeks · Scroll horizontally to explore the season
        </span>
        <span className="inline-flex items-center gap-1.5">
          <LockKeyhole size={12} />
          Other rows are calculated or read-only references
        </span>
      </div>
    </>
  )
}
